/**
 * Centralized error logging service.
 *
 * - Singleton: import { errorLogger } from "@/lib/errorLogger"
 * - Batches errors and flushes to Supabase via the log_error_deduplicated RPC
 * - Rate-limited (10/min), client-side deduped (60s window), fail-safe (never throws)
 * - Strips sensitive fields (password, token, secret, api_key)
 */

import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

// ── Types ──────────────────────────────────────────────────────────────────

export type ErrorType =
  | "render" | "network" | "supabase_query" | "supabase_rpc"
  | "edge_function" | "auth" | "validation" | "navigation"
  | "unhandled" | "ios_service" | "ios_crash" | "user_report" | "unknown";

export type ErrorSeverity = "critical" | "error" | "warning" | "info";

export interface ErrorLogPayload {
  error_type: ErrorType;
  severity?: ErrorSeverity;
  message: string;
  stack_trace?: string | null;
  error_code?: string | null;
  component_name?: string | null;
  action_context?: string | null;
  module?: string | null;
  request_method?: string | null;
  request_url?: string | null;
  request_payload?: Record<string, unknown> | null;
  response_status?: number | null;
  response_body?: string | null;
}

interface Breadcrumb {
  action: string;
  timestamp: string;
  detail?: string;
}

interface QueuedError {
  payload: ErrorLogPayload;
  fingerprint: string;
}

const MODULE_PATH_MAP: Record<string, string> = {
  "/warehouse": "warehouse", "/manufacturing": "manufacturing",
  "/quality": "quality", "/npd": "npd", "/purchasing": "purchasing",
  "/crm": "crm", "/admin": "admin", "/equipment": "equipment",
  "/training": "training", "/shipping": "shipping", "/documents": "documents",
  "/suppliers": "suppliers", "/stock-items": "stock_items",
  "/planning": "planning", "/tasks": "tasks", "/messages": "messages",
  "/ai-chat": "ai_chat",
};

const SENSITIVE_KEYS = new Set([
  "password", "token", "secret", "api_key", "apikey",
  "authorization", "access_token", "refresh_token",
  "credit_card", "ssn", "cvv",
]);

class ErrorLogger {
  private static instance: ErrorLogger;
  private queue: QueuedError[] = [];
  private breadcrumbs: Breadcrumb[] = [];
  private recentFingerprints = new Map<string, number>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private errorCount = 0;
  private errorCountResetTimer: ReturnType<typeof setInterval> | null = null;
  private userId: string | null = null;
  private userName: string | null = null;
  private userEmail: string | null = null;

  private readonly MAX_BREADCRUMBS = 20;
  private readonly MAX_QUEUE_SIZE = 10;
  private readonly FLUSH_INTERVAL_MS = 5_000;
  private readonly RATE_LIMIT_PER_MINUTE = 10;
  private readonly DEDUP_WINDOW_MS = 60_000;
  private readonly MAX_STACK_TRACE_LENGTH = 4000;
  private readonly MAX_RESPONSE_BODY_LENGTH = 2000;

  private constructor() {
    this.errorCountResetTimer = setInterval(() => {
      this.errorCount = 0;
      const cutoff = Date.now() - this.DEDUP_WINDOW_MS;
      for (const [fp, ts] of this.recentFingerprints) {
        if (ts < cutoff) this.recentFingerprints.delete(fp);
      }
    }, 60_000);
  }

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) ErrorLogger.instance = new ErrorLogger();
    return ErrorLogger.instance;
  }

  setUser(userId: string | null, userName?: string | null, userEmail?: string | null): void {
    this.userId = userId;
    this.userName = userName ?? null;
    this.userEmail = userEmail ?? null;
  }

  addBreadcrumb(action: string, detail?: string): void {
    try {
      this.breadcrumbs.push({ action, timestamp: new Date().toISOString(), detail });
      if (this.breadcrumbs.length > this.MAX_BREADCRUMBS) this.breadcrumbs.shift();
    } catch { /* never throw */ }
  }

  /** Return a copy of current breadcrumbs for bug report inclusion. */
  getBreadcrumbs(): Breadcrumb[] {
    return [...this.breadcrumbs];
  }

  log(payload: ErrorLogPayload): void {
    try {
      if (this.isRateLimited()) return;
      const fingerprint = this.generateFingerprint(payload);
      if (this.isDuplicate(fingerprint)) return;
      this.errorCount++;
      this.recentFingerprints.set(fingerprint, Date.now());

      const enriched: ErrorLogPayload = {
        ...payload,
        severity: payload.severity || this.inferSeverity(payload),
        module: payload.module || this.inferModule(),
        stack_trace: payload.stack_trace?.slice(0, this.MAX_STACK_TRACE_LENGTH),
        response_body: payload.response_body?.slice(0, this.MAX_RESPONSE_BODY_LENGTH),
      };

      // Forward to Sentry with structured context so both systems stay in sync
      this.forwardToSentry(enriched);

      this.queue.push({ payload: enriched, fingerprint });
      if (this.queue.length >= this.MAX_QUEUE_SIZE) { this.flush(); }
      else { this.scheduleFlush(); }
    } catch { /* never throw */ }
  }

  logError(message: string, opts?: Partial<ErrorLogPayload>): void {
    this.log({ error_type: "unknown", message, severity: "error", ...opts });
  }

  logWarning(message: string, opts?: Partial<ErrorLogPayload>): void {
    this.log({ error_type: "unknown", message, severity: "warning", ...opts });
  }

  logCritical(message: string, opts?: Partial<ErrorLogPayload>): void {
    this.log({ error_type: "unknown", message, severity: "critical", ...opts });
  }

  private forwardToSentry(payload: ErrorLogPayload): void {
    try {
      const sentryLevel = payload.severity === "critical" ? "fatal"
        : payload.severity === "warning" ? "warning"
        : payload.severity === "info" ? "info"
        : "error";

      Sentry.withScope((scope) => {
        scope.setLevel(sentryLevel);
        scope.setTag("error_type", payload.error_type);
        if (payload.module) scope.setTag("qf.module", payload.module);
        if (payload.component_name) scope.setTag("qf.component", payload.component_name);
        if (payload.action_context) scope.setTag("qf.action", payload.action_context);
        if (payload.error_code) scope.setTag("qf.error_code", payload.error_code);
        if (payload.request_url) scope.setContext("request", {
          method: payload.request_method,
          url: payload.request_url,
          status: payload.response_status,
        });
        scope.setFingerprint([payload.error_type, payload.message]);
        Sentry.captureMessage(payload.message, sentryLevel);
      });
    } catch { /* never throw from error logging */ }
  }

  private generateFingerprint(payload: ErrorLogPayload): string {
    const raw = `${payload.message}|${payload.component_name || ""}|${payload.error_code || ""}|${payload.error_type}`;
    let hash = 0;
    for (let i = 0; i < raw.length; i++) { hash = ((hash << 5) - hash + raw.charCodeAt(i)) | 0; }
    return String(Math.abs(hash));
  }

  private isDuplicate(fingerprint: string): boolean {
    const lastSeen = this.recentFingerprints.get(fingerprint);
    return lastSeen ? Date.now() - lastSeen < this.DEDUP_WINDOW_MS : false;
  }

  private isRateLimited(): boolean { return this.errorCount >= this.RATE_LIMIT_PER_MINUTE; }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => { this.flushTimer = null; this.flush(); }, this.FLUSH_INTERVAL_MS);
  }

  private async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0, this.MAX_QUEUE_SIZE);
    for (const item of batch) {
      try {
        await supabase.rpc("log_error_deduplicated", {
          p_payload: ({
            error_type: item.payload.error_type,
            severity: item.payload.severity || "error",
            message: item.payload.message,
            stack_trace: item.payload.stack_trace || null,
            error_code: item.payload.error_code || null,
            platform: "web",
            user_id: this.userId || null,
            user_name: this.userName || null,
            user_email: this.userEmail || null,
            page_url: typeof window !== "undefined" ? window.location.href : null,
            component_name: item.payload.component_name || null,
            action_context: item.payload.action_context || null,
            module: item.payload.module || null,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            app_version: null,
            device_info: this.getDeviceInfo(),
            request_method: item.payload.request_method || null,
            request_url: item.payload.request_url || null,
            request_payload: item.payload.request_payload ? this.sanitizePayload(item.payload.request_payload) : null,
            response_status: item.payload.response_status || null,
            response_body: item.payload.response_body || null,
            fingerprint: item.fingerprint,
            breadcrumbs: [...this.breadcrumbs],
          }) as unknown as Json,
        });
      } catch { /* silently fail */ }
    }
  }

  private inferSeverity(payload: ErrorLogPayload): ErrorSeverity {
    if (payload.error_type === "render" || payload.error_type === "unhandled") return "critical";
    if (payload.error_type === "auth") return "critical";
    if (payload.error_type === "network" && !payload.response_status) return "error";
    if (payload.response_status && payload.response_status >= 500) return "error";
    if (payload.response_status && payload.response_status >= 400) return "warning";
    return "error";
  }

  private inferModule(): string | null {
    if (typeof window === "undefined") return null;
    const path = window.location.pathname;
    for (const [prefix, mod] of Object.entries(MODULE_PATH_MAP)) {
      if (path.startsWith(prefix)) return mod;
    }
    if (path === "/") return "dashboard";
    return null;
  }

  private getDeviceInfo(): Record<string, string | number | boolean> {
    if (typeof window === "undefined") return {};
    return {
      screen_width: window.screen?.width, screen_height: window.screen?.height,
      viewport_width: window.innerWidth, viewport_height: window.innerHeight,
      pixel_ratio: window.devicePixelRatio, language: navigator.language,
      online: navigator.onLine,
    };
  }

  private sanitizePayload(payload: Record<string, unknown>): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(payload)) {
      if (SENSITIVE_KEYS.has(key.toLowerCase())) { sanitized[key] = "[REDACTED]"; }
      else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        sanitized[key] = this.sanitizePayload(value as Record<string, unknown>);
      } else { sanitized[key] = value; }
    }
    return sanitized;
  }
}

export const errorLogger = ErrorLogger.getInstance();
